import clsx from 'clsx';
import React, {
  cloneElement,
  createElement,
  FC,
  forwardRef,
  FunctionComponent,
  HTMLProps,
  isValidElement,
  PropsWithChildren,
  ReactHTML,
} from 'react';
import classes from './list.module.scss';

export type ListProps<P extends object = HTMLProps<HTMLLIElement>> = PropsWithChildren<
  Omit<HTMLProps<HTMLDivElement>, 'as'> & {
    /**
     * Remove top and bottom padding for list
     */
    noPadding?: boolean;

    /**
     * Specifies the variation type for a component or element.
     *
     * Available options:
     * - `'decorated'`: Indicates a variant styled or presented in a decorated manner.
     * - `'numbered'`: Indicates a variant styled or presented with numbering.
     *
     * This property is optional.
     */
    variant?: 'decorated' | 'numbered';

    /**
     * Customize render element
     */
    as?: FunctionComponent<P> | keyof ReactHTML;

    /**
     * `normal` is the default spacing
     * `compact` reduces padding
     */
    spacing?: 'normal' | 'compact';
  }
>;

export const List: FC<ListProps> = forwardRef<
  HTMLDivElement,
  ListProps
>(({
  as = 'div',
  children,
  noPadding,
  variant,
  spacing = 'normal',
  ...props
}, ref) => {
  return createElement(
    variant === 'numbered' ? 'ol' : as as keyof ReactHTML,
    {
      ...props,
      ref,
      role: 'list',
      className: clsx(classes.root, props.className, {
        [classes.noPadding]: noPadding,
        [classes.decorated]: variant === 'decorated',
        [classes.numbered]: variant === 'numbered',
        [classes.compact]: spacing === 'compact',
      }),
    },
    React.Children.map(children, child => {
      if (isValidElement(child)) {
        return cloneElement(child, {
          ...child.props,
          className: clsx(child.props.className, classes.item),
          variant: child.props.variant ?? spacing === 'compact' ? 'compact' : undefined,
        });
      }
      return child;
    })
  );
});
